import React, { Fragment, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRight";
import LocalFloristRoundedIcon from "@mui/icons-material/LocalFloristRounded";
import { buildFormulaRiskMatrix, summarizeFormulaRiskMatrix } from "../platform/riskMatrix";
import { euAllergens } from "../platform/compliance";
import { SourceCitation, SourceQuoteTooltip, StatusChip } from "./ui/PlatformPrimitives";

// EU Annex III declared-allergen CAS lookup. Used by the aggregate panel to
// flag cross-oil totals that cross the labelling thresholds. Leave-on is the
// stricter limit (fragrance / EDP / lotion), rinse-off is more permissive.
const ALLERGEN_CAS_INDEX = (() => {
  const map = new Map();
  euAllergens.forEach((entry) => {
    const cas = String(entry.cas || "").trim();
    if (!cas) return;
    if (!map.has(cas)) map.set(cas, { name: entry.inciName || entry.name, source: entry.source, sourceUrl: entry.sourceUrl, sourceQuote: entry.sourceQuote });
  });
  return map;
})();
const EU_ALLERGEN_LEAVE_ON_PCT = 0.001;
const EU_ALLERGEN_RINSE_OFF_PCT = 0.01;

const aggregateSeverity = (agg) => {
  const meta = ALLERGEN_CAS_INDEX.get(String(agg.casNumber || "").trim());
  if (!meta) return { state: "info", meta: null };
  if (agg.total >= EU_ALLERGEN_RINSE_OFF_PCT) return { state: "above-leaveon", meta };
  if (agg.total >= EU_ALLERGEN_LEAVE_ON_PCT) return { state: "above-leaveon", meta };
  if (agg.total >= EU_ALLERGEN_LEAVE_ON_PCT / 10) return { state: "near-leaveon", meta };
  return { state: "below", meta };
};

const formatPercentage = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(2) : value;

// Liquid Glass risk-state palette — uses translucent tints (rgba) so each
// chip blends with the table cell's glass surface while keeping the
// foreground text weight at 600 for ≥ 4.5:1 contrast on light backdrops.
const cellMeta = {
  clear: { label: "Clear", bg: "rgba(45,106,79,0.14)", color: "#1B4332", border: "rgba(45,106,79,0.28)" },
  review: { label: "Review", bg: "rgba(146,64,14,0.14)", color: "#5C2A06", border: "rgba(146,64,14,0.28)" },
  block: { label: "Block", bg: "rgba(153,27,27,0.14)", color: "#5C0F0F", border: "rgba(153,27,27,0.28)" },
  na: { label: "N/A", bg: "rgba(0,0,0,0.05)", color: "rgba(0,0,0,0.55)", border: "rgba(0,0,0,0.10)" },
};

// Aggregate every source quote across this cell's issues into a single
// `sourceQuotes` array so the tooltip shows the same provenance the
// EvidencePanel's <SourceCitation> pills would for those issues.
const collectQuotes = (issues = []) => {
  const collected = [];
  issues.forEach((issue) => {
    if (Array.isArray(issue.sourceQuotes) && issue.sourceQuotes.length) {
      issue.sourceQuotes.forEach((q) => {
        collected.push({
          quote: q.quote || q.text || "",
          source: q.source || issue.source,
          sourceUrl: q.sourceUrl || issue.sourceUrl || "",
          label: q.label || "",
        });
      });
    } else if (issue.sourceQuote) {
      collected.push({
        quote: issue.sourceQuote,
        source: issue.source,
        sourceUrl: issue.sourceUrl || "",
      });
    }
  });
  // Deduplicate identical (source, quote) pairs so we don't show the same
  // IFRA category quote twice when a row has both unit + threshold issues.
  const seen = new Set();
  return collected.filter((entry) => {
    const key = `${entry.source}|${entry.quote}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const RiskCell = ({ cell }) => {
  const meta = cellMeta[cell.state] || cellMeta.na;
  const quotes = collectQuotes(cell.issues);
  const chip = (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        // Pill shape via theme; explicit colors override theme chip palette.
        // fontWeight bumped to 600 so the label still reads on translucent
        // table cells (Liquid Glass accessibility rule).
        width: 68,
        bgcolor: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.border}`,
        borderRadius: 999,
        fontWeight: 600,
      }}
    />
  );
  // Every cell renders with a tooltip: pull-quote(s) when issues carry
  // source provenance, otherwise the plain `detail` text (e.g. "Above
  // Category 4 threshold 1.7%") so designers always see why a cell is in
  // its current state on hover.
  return (
    <SourceQuoteTooltip
      sourceQuotes={quotes}
      fallback={`${cell.label}: ${cell.detail || meta.label}`}
    >
      {chip}
    </SourceQuoteTooltip>
  );
};

const EvidencePanel = ({ row }) => {
  const theme = useTheme();
  if (!row) {
    return (
      <Paper sx={{ p: 3, height: "100%", minHeight: 200, borderRadius: 2.25 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", position: "relative", zIndex: 1 }}>
          Select a row to view evidence.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: "100%", minHeight: 200, position: { lg: "sticky" }, top: 24, borderRadius: 2.25 }}>
      {/* All inner content sits above the Paper's specular ::before. */}
      <Stack spacing={2} sx={{ position: "relative", zIndex: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ color: "text.primary" }}>{row.ingredient.name}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {row.ingredient.casNumber && row.ingredient.casNumber !== "N/A" ? row.ingredient.casNumber : "CAS not verified"}
            </Typography>
          </Box>
          <StatusChip status={row.overall.state === "block" ? "Blocked" : row.overall.state === "review" ? "Review" : "Clear"} />
        </Stack>

        <Box sx={{ display: "grid", gap: 1 }}>
          {row.cells.map((cell) => (
            <Box
              key={cell.key}
              sx={{
                display: "grid",
                gridTemplateColumns: "68px 1fr",
                gap: 1.5,
                alignItems: "start",
                py: 1,
                // Translucent hairline divider — theme glass hairline pattern.
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <RiskCell cell={cell} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>{cell.label}</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>{cell.detail}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {row.issues.length > 0 && (
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 600, color: "text.secondary" }}>
              {row.issues.length} issue{row.issues.length !== 1 ? "s" : ""}
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {row.issues.map((issue, index) => (
                // Inner card: glass-inset tier (12 radius = concentric inside a
                // 16/18 parent), pill-style chip carries the severity tint.
                <Paper
                  key={`${issue.code}-${index}`}
                  sx={{
                    p: 2,
                    // Inset tier (12) inside the EvidencePanel chrome (18).
                    borderRadius: 1.5,
                    // Theme inset glass tokens — replaces the previously
                    // hardcoded translucent background / blur / hairline.
                    background: theme.glass.inset.background,
                    backdropFilter: theme.glass.inset.backdropFilter,
                    WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
                    border: theme.glass.inset.border,
                    boxShadow: theme.glass.inset.boxShadow,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, position: "relative", zIndex: 1 }}>
                    <Chip
                      label={issue.blocking ? "blocking" : "warning"}
                      size="small"
                      sx={{
                        // Translucent severity tint over inset glass; dark
                        // text shade is the codebase's canonical severity-
                        // contrast text colour (matches theme MuiChip
                        // colorError / colorWarning definitions).
                        bgcolor: issue.blocking ? "rgba(153,27,27,0.14)" : "rgba(146,64,14,0.14)",
                        color: issue.blocking ? "#5C0F0F" : "#5C2A06",
                        border: `1px solid ${issue.blocking ? "rgba(153,27,27,0.28)" : "rgba(146,64,14,0.28)"}`,
                        borderRadius: 999,
                        fontWeight: 600,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "monospace" }}>{issue.code}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ position: "relative", zIndex: 1, color: "text.primary" }}>{issue.message}</Typography>
                  <Box sx={{ mt: 1, position: "relative", zIndex: 1 }}>
                    <SourceCitation source={issue.source} sourceUrl={issue.sourceUrl} sourceQuote={issue.sourceQuote} sourceQuotes={issue.sourceQuotes} highlight={issue.highlight} />
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

// Build the per-oil constituent map from the unfolded compositional data.
// Keyed by lowercased oil name so we can match a matrix row (which uses the
// IFRA dataset's `ingredient.name`) to its derived constituents.
const buildConstituentIndex = (report) => {
  const map = new Map();
  (report?.unfolded?.constituents || []).forEach((c) => {
    const key = String(c.derivedFrom?.oilName || "").toLowerCase();
    if (!key) return;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(c);
  });
  return map;
};

const constituentRiskState = (constituent) => {
  if (constituent.above_threshold) return "block";
  if (!constituent.validIngredient) return "review";
  return "clear";
};

const ConstituentChip = ({ constituent }) => {
  const state = constituentRiskState(constituent);
  const meta = cellMeta[state] || cellMeta.na;
  const detail = constituent.above_threshold
    ? `Above IFRA Cat 4 threshold ${constituent.category_4_threshold}%`
    : constituent.category_4_threshold && constituent.category_4_threshold !== "N/A"
      ? `Within IFRA Cat 4 (${constituent.category_4_threshold}% limit)`
      : "No IFRA Cat 4 limit on this constituent";
  return (
    <SourceQuoteTooltip
      fallback={`${constituent.ingredient_name}: ${detail}`}
    >
      <Chip
        label={meta.label}
        size="small"
        sx={{
          width: 68,
          bgcolor: meta.bg,
          color: meta.color,
          border: `1px solid ${meta.border}`,
          borderRadius: 999,
          fontWeight: 600,
        }}
      />
    </SourceQuoteTooltip>
  );
};

const aggregateChipStyle = (state) => {
  if (state === "above-leaveon") {
    return { bgcolor: "rgba(153,27,27,0.14)", border: "1px solid rgba(153,27,27,0.28)", color: "#5C0F0F", borderRadius: 999, fontWeight: 600 };
  }
  if (state === "near-leaveon") {
    return { bgcolor: "rgba(146,64,14,0.14)", border: "1px solid rgba(146,64,14,0.28)", color: "#5C2A06", borderRadius: 999, fontWeight: 600 };
  }
  if (state === "below") {
    return { bgcolor: "rgba(45,106,79,0.14)", border: "1px solid rgba(45,106,79,0.28)", color: "#1B4332", borderRadius: 999, fontWeight: 600 };
  }
  return { bgcolor: "rgba(255,255,255,0.45)", border: "1px solid rgba(0,0,0,0.10)", color: "rgba(0,0,0,0.78)", borderRadius: 999, fontWeight: 600 };
};

// Cross-oil aggregate strip — sums every constituent across every oil in
// the formula, so the user sees that "Linalool: 1.74% from 3 oils" before
// drilling into any single oil. SOTA tools surface this _above_ the
// matrix because allergen limits actually apply to the cross-oil sum,
// not to any single oil's contribution.
const AggregatePanel = ({ aggregates }) => {
  if (!aggregates || aggregates.length === 0) return null;
  const decorated = aggregates
    .map((agg) => ({ ...agg, ...aggregateSeverity(agg) }))
    .sort((a, b) => {
      // Allergens-above-threshold first, then near-threshold, then by total.
      const rank = { "above-leaveon": 3, "near-leaveon": 2, below: 1, info: 0 };
      const r = (rank[b.state] || 0) - (rank[a.state] || 0);
      if (r !== 0) return r;
      return b.total - a.total;
    })
    .slice(0, 14);
  const flaggedCount = decorated.filter((a) => a.state === "above-leaveon").length;
  return (
    // Aggregate panel sits on Paper → auto-glass via theme. The chip strip
    // stays horizontally scrollable; only the surface tier changes here.
    <Paper sx={{ p: { xs: 2, md: 2.5 }, animation: "fadeIn 350ms ease both", minWidth: 0, width: "100%", borderRadius: 2 }}>
      <Stack spacing={1.5} sx={{ minWidth: 0, position: "relative", zIndex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ fontWeight: 600, color: "text.secondary" }}>
            Aggregate constituents · across all oils in this formula
          </Typography>
          {flaggedCount > 0 && (
            <Chip
              size="small"
              label={`${flaggedCount} above EU leave-on threshold`}
              // Severity-error pill: translucent tint + darker readable text
              // (codebase severity-contrast convention, matches theme chip
              // colorError). Pill radius 999 per rule 7.
              sx={{ bgcolor: "rgba(153,27,27,0.14)", color: "#5C0F0F", border: "1px solid rgba(153,27,27,0.28)", borderRadius: 999, fontWeight: 600 }}
            />
          )}
        </Stack>
        {/* Horizontal scroll instead of flex-wrap — keeps the panel a fixed
            height (1 chip row) and lets users scroll right to see less-
            critical aggregates instead of having the panel grow tall. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          sx={{
            minWidth: 0,
            flexWrap: "nowrap",
            overflowX: "auto",
            overflowY: "hidden",
            pb: 0.5,
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.18)",
              borderRadius: 999,
            },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "& > *": { flexShrink: 0 },
          }}
        >
          {decorated.map((agg) => {
            const style = aggregateChipStyle(agg.state);
            // No hover tooltip on the aggregate strip — the chip's label
            // ("Linalool 1.74%") plus its colour state is the entire signal.
            // Drill into the matrix below for per-oil contributors and the
            // EU Annex III citation when one is needed.
            return (
              <Chip
                key={agg.casNumber}
                size="small"
                label={`${agg.name} ${agg.total.toFixed(2)}%`}
                sx={{
                  fontFamily: "monospace",
                  maxWidth: 240,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  ...style,
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

const FormulaRiskMatrix = ({ report }) => {
  const theme = useTheme();
  const rows = useMemo(() => buildFormulaRiskMatrix(report), [report]);
  const summary = useMemo(() => summarizeFormulaRiskMatrix(rows), [rows]);
  const constituentIndex = useMemo(() => buildConstituentIndex(report), [report]);
  const [selectedId, setSelectedId] = useState("");
  const [expandedOilIds, setExpandedOilIds] = useState(new Set());
  const selectedRow = rows.find((row) => row.id === selectedId) || rows[0] || null;

  if (!report || !rows.length) return null;

  const toggleExpanded = (id) => {
    setExpandedOilIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <AggregatePanel aggregates={report?.unfolded?.aggregates || []} />
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "minmax(0, 1fr) 360px" }, gap: 2 }}>
      <Paper sx={{ overflow: "hidden", animation: "fadeInUp 520ms ease both", minWidth: 0, borderRadius: 2.25 }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 1.5, md: 2 },
            // Translucent hairline divider — uses the theme rgba pattern so
            // the rule reads against glass rather than a solid surface.
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            // Inset glass tier — concentric tinted band sitting inside the
            // outer Paper chrome; pulled from the theme token.
            background: theme.glass.inset.background,
            backdropFilter: theme.glass.inset.backdropFilter,
            WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h6" sx={{ color: "text.primary" }}>Formula Risk Matrix</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Ingredient-level screening across unit, identity, IFRA, EU Annex, and allergen risk.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {/* Severity summary chips — translucent tints with the codebase's
                canonical darker severity-contrast text shades (matches
                theme.js MuiChip colorSuccess / colorWarning / colorError). */}
            <Chip label={`${summary.blocked} blocked`} size="small" sx={{ bgcolor: "rgba(153,27,27,0.14)", color: "#5C0F0F", border: "1px solid rgba(153,27,27,0.28)", borderRadius: 999, fontWeight: 600 }} />
            <Chip label={`${summary.review} review`} size="small" sx={{ bgcolor: "rgba(146,64,14,0.14)", color: "#5C2A06", border: "1px solid rgba(146,64,14,0.28)", borderRadius: 999, fontWeight: 600 }} />
            <Chip label={`${summary.clear} clear`} size="small" sx={{ bgcolor: "rgba(45,106,79,0.14)", color: "#1B4332", border: "1px solid rgba(45,106,79,0.28)", borderRadius: 999, fontWeight: 600 }} />
          </Stack>
        </Box>

        <Box sx={{ overflowX: "auto", width: "100%", maxWidth: "100%", position: "relative", zIndex: 1 }}>
          <Table size="small" sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow>
                <TableCell>Ingredient</TableCell>
                <TableCell>CAS</TableCell>
                <TableCell align="right">%</TableCell>
                <TableCell align="center">Unit</TableCell>
                <TableCell align="center">Identity</TableCell>
                <TableCell align="center">IFRA</TableCell>
                <TableCell align="center">EU Annex</TableCell>
                <TableCell align="center">Allergen</TableCell>
                <TableCell align="center">Overall</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const cellsByKey = Object.fromEntries(row.cells.map((cell) => [cell.key, cell]));
                const selected = row.id === selectedRow?.id;
                const oilName = String(row.ingredient.name || "").toLowerCase();
                const constituents = constituentIndex.get(oilName) || [];
                const expandable = constituents.length > 0;
                const expanded = expandedOilIds.has(row.id);
                const flaggedConstituents = constituents.filter(
                  (c) => c.above_threshold
                ).length;
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      hover
                      onClick={() => setSelectedId(row.id)}
                      sx={{
                        cursor: "pointer",
                        // Selected row gets a faint white tint that reads as
                        // a translucent overlay rather than a solid bar.
                        bgcolor: selected ? "rgba(255,255,255,0.45)" : "transparent",
                        "& td:first-of-type": {
                          // Severity rail uses the same dark severity hues as
                          // the rest of the Liquid Glass palette so the 3px
                          // accent composes with the row's translucent surface.
                          borderLeft: `3px solid ${
                            row.overall.state === "block"
                              ? "rgba(153,27,27,0.85)"
                              : row.overall.state === "review"
                                ? "rgba(146,64,14,0.75)"
                                : "rgba(45,106,79,0.75)"
                          }`,
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {expandable ? (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(row.id);
                              }}
                              aria-label={expanded ? "Collapse constituents" : "Expand constituents"}
                              sx={{ p: 0.25 }}
                            >
                              {expanded ? (
                                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <KeyboardArrowRightRoundedIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 26 }} />
                          )}
                          {expandable && (
                            <LocalFloristRoundedIcon
                              sx={{ fontSize: 14, color: "rgba(122,99,29,0.85)" }}
                              titleAccess="Essential oil — composition unfolded"
                            />
                          )}
                          <span>{row.ingredient.name}</span>
                          {expandable && (
                            <Chip
                              size="small"
                              label={
                                flaggedConstituents > 0
                                  ? `${constituents.length} constituents · ${flaggedConstituents} flagged`
                                  : `${constituents.length} constituents`
                              }
                              sx={{
                                ml: 0.5,
                                height: 20,
                                fontWeight: 600,
                                borderRadius: 999,
                                // Severity-error tint when flagged; otherwise
                                // navy brand tint (allowlisted #1B2A4A text on
                                // a navy-derived rgba background).
                                bgcolor: flaggedConstituents > 0 ? "rgba(153,27,27,0.14)" : "rgba(27,42,74,0.10)",
                                color: flaggedConstituents > 0 ? "#5C0F0F" : "#1B2A4A",
                                border: `1px solid ${flaggedConstituents > 0 ? "rgba(153,27,27,0.28)" : "rgba(27,42,74,0.15)"}`,
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>{row.ingredient.casNumber || "N/A"}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                        {row.ingredient.formulaPercentage !== null && row.ingredient.formulaPercentage !== undefined
                          ? formatPercentage(row.ingredient.formulaPercentage)
                          : "—"}
                      </TableCell>
                      <TableCell align="center"><RiskCell cell={cellsByKey.unit} /></TableCell>
                      <TableCell align="center"><RiskCell cell={cellsByKey.identity} /></TableCell>
                      <TableCell align="center"><RiskCell cell={cellsByKey.ifra} /></TableCell>
                      <TableCell align="center"><RiskCell cell={cellsByKey.euAnnex} /></TableCell>
                      <TableCell align="center"><RiskCell cell={cellsByKey.allergen} /></TableCell>
                      <TableCell align="center"><RiskCell cell={row.overall} /></TableCell>
                    </TableRow>
                    {expandable && expanded && constituents.map((constituent) => {
                      const cas = String(constituent.cas_number || "").trim();
                      const isAllergen = ALLERGEN_CAS_INDEX.has(cas);
                      const hasIfraLimit =
                        constituent.category_4_threshold &&
                        constituent.category_4_threshold !== "N/A";
                      const ifraState = constituent.above_threshold
                        ? "block"
                        : hasIfraLimit
                          ? "clear"
                          : "na";
                      const ifraLabel = constituent.above_threshold
                        ? `Above IFRA Cat 4 ${constituent.category_4_threshold}%`
                        : hasIfraLimit
                          ? `Within IFRA Cat 4 (${constituent.category_4_threshold}%)`
                          : "No IFRA Cat 4 standard for this constituent";
                      return (
                        <TableRow
                          key={`${row.id}::${constituent.cas_number}`}
                          sx={{
                            // Inset glass row — translucent white that visually
                            // recedes inside the parent Paper without going
                            // opaque. Dashed hairline keeps the expanded
                            // children visually nested under the oil row.
                            bgcolor: "rgba(255,255,255,0.30)",
                            "& td": { borderBottom: "1px dashed rgba(0,0,0,0.06)", py: 1 },
                          }}
                        >
                          <TableCell sx={{ pl: 5 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ width: 8, height: 1, bgcolor: "rgba(0,0,0,0.30)" }} />
                              <Typography variant="body2" component="span" sx={{ color: "text.primary" }}>{constituent.ingredient_name}</Typography>
                              {isAllergen && (
                                <Chip
                                  size="small"
                                  label="EU allergen"
                                  sx={{
                                    height: 18,
                                    fontWeight: 600,
                                    borderRadius: 999,
                                    // Translucent warning tint + codebase severity-contrast text.
                                    bgcolor: "rgba(146,64,14,0.14)",
                                    color: "#5C2A06",
                                    border: "1px solid rgba(146,64,14,0.28)",
                                  }}
                                />
                              )}
                              <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                                {Number(constituent.derivedFrom?.oilPercentage).toFixed(2)}% × {Number(constituent.derivedFrom?.constituentPercent).toFixed(2)}% typical
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>
                            <Typography variant="caption" component="span" sx={{ fontFamily: "monospace" }}>
                              {constituent.cas_number || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                            <Typography variant="body2" component="span" sx={{ fontFamily: "monospace", color: "text.primary" }}>
                              {Number.isFinite(Number(constituent.formula_percentage))
                                ? Number(constituent.formula_percentage).toFixed(3)
                                : "—"}
                            </Typography>
                          </TableCell>
                          {/* Constituent rows reuse the parent oil's Unit/Identity
                              status — only the IFRA + Allergen + Overall columns
                              meaningfully differ at the constituent level. */}
                          <TableCell align="center">
                            <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <SourceQuoteTooltip fallback={`${constituent.ingredient_name}: ${ifraLabel}`}>
                              <Chip
                                label={cellMeta[ifraState].label}
                                size="small"
                                sx={{
                                  width: 68,
                                  bgcolor: cellMeta[ifraState].bg,
                                  color: cellMeta[ifraState].color,
                                  border: `1px solid ${cellMeta[ifraState].border}`,
                                  borderRadius: 999,
                                  fontWeight: 600,
                                }}
                              />
                            </SourceQuoteTooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <SourceQuoteTooltip
                              fallback={
                                isAllergen
                                  ? `${constituent.ingredient_name} is an EU Annex III allergen — see aggregate panel for cross-oil total.`
                                  : `${constituent.ingredient_name}: not on the EU Annex III declared-allergen list`
                              }
                            >
                              <Chip
                                label={isAllergen ? "Review" : "—"}
                                size="small"
                                sx={{
                                  width: 68,
                                  // Translucent warning tint when allergen; otherwise inset glass.
                                  bgcolor: isAllergen ? "rgba(146,64,14,0.14)" : "rgba(0,0,0,0.04)",
                                  color: isAllergen ? "#5C2A06" : "rgba(0,0,0,0.45)",
                                  border: `1px solid ${isAllergen ? "rgba(146,64,14,0.28)" : "rgba(0,0,0,0.10)"}`,
                                  borderRadius: 999,
                                  fontWeight: isAllergen ? 600 : 500,
                                }}
                              />
                            </SourceQuoteTooltip>
                          </TableCell>
                          <TableCell align="center"><ConstituentChip constituent={constituent} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <EvidencePanel row={selectedRow} />
    </Box>
    </Stack>
  );
};

export default FormulaRiskMatrix;
